import { create } from 'zustand';
import { getAccountInfo, getCreditCardInfoV3, putCreditCardInfoV3 } from '../services/accountApi';
import {
  detectCardBrand,
  isSupportedLength,
  isValidCardNumber,
  normalizeCardNumber,
} from '../utils/cardValidation';
import { setSessionUser, type SessionUser } from '../utils/session';
import { useRefillsStore } from './useRefillsStore';
import type {
  BillingFormValues,
  BillingStore,
  BillingUpdatePayload,
  EprotectResponse,
  ExistingCardInfo,
} from './types';

const DEFAULT_CC_HINT = 'LOADING CARD DATA...';
const NO_CARD_HINT = 'No Card On File';

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asInteger(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function normalizeExpMonth(value: unknown): string {
  const parsed = asInteger(value);
  if (parsed !== null && parsed >= 1 && parsed <= 12) {
    return String(parsed);
  }

  return asString(value, '');
}

function normalizeExpYear(value: unknown): string {
  const parsed = asInteger(value);
  if (parsed !== null) {
    if (parsed >= 2000 && parsed <= 2099) {
      return String(parsed - 2000);
    }

    if (parsed >= 0 && parsed <= 99) {
      return String(parsed);
    }
  }

  return asString(value, '');
}

function normalizeErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
}

function toFormValues(existingCard: ExistingCardInfo | null): BillingFormValues {
  if (!existingCard) {
    return {
      CardNumber: '',
      ExpMonth: '',
      ExpYear: '',
      cardholderFullName: '',
      Street1: '',
      Street2: '',
      City: '',
      State: '',
      Zip: '',
    };
  }

  const firstName = asString(existingCard.FirstName, '').trim();
  const lastName = asString(existingCard.LastName, '').trim();

  return {
    CardNumber: '',
    ExpMonth: normalizeExpMonth(existingCard.ExpMonth),
    ExpYear: normalizeExpYear(existingCard.ExpYear),
    cardholderFullName: `${firstName} ${lastName}`.trim(),
    Street1: asString(existingCard.Street1, ''),
    Street2: asString(existingCard.Street2, ''),
    City: asString(existingCard.City, ''),
    State: asString(existingCard.State, ''),
    Zip: asString(existingCard.Zip, ''),
  };
}

function buildCcHint(cardNumber: string): string {
  if (!cardNumber) {
    return NO_CARD_HINT;
  }

  return `${cardNumber}      Re-enter Card Number To Update`;
}

function buildPayloadFromForm(
  values: BillingFormValues,
  ccType: string | null
): BillingUpdatePayload {
  const [firstName, ...rest] = values.cardholderFullName.trim().split(/\s+/);
  const lastName = rest.join(' ');
  const cardNumber = normalizeCardNumber(values.CardNumber);

  return {
    CardType: ccType || undefined,
    CardholderName: values.cardholderFullName.trim(),
    ExpMonth: Number(values.ExpMonth) || undefined,
    ExpYear: Number(values.ExpYear) || undefined,
    FirstName: firstName || undefined,
    LastName: lastName || undefined,
    Street1: values.Street1 || undefined,
    Street2: values.Street2 || undefined,
    City: values.City || undefined,
    State: values.State || undefined,
    Zip: values.Zip ? values.Zip.substring(0, 5) : undefined,
    First10: cardNumber ? cardNumber.slice(0, 10) : undefined,
    Last4: cardNumber ? cardNumber.slice(-4) : undefined,
    OrderId: 'order123',
    ReportGroup: 'MPBI',
  };
}

const initialState = {
  existingCard: null as import('./types').ExistingCardInfo | null,
  formValues: toFormValues(null),
  originalFormValues: toFormValues(null),
  nyDisclaimerAccepted: false,
  ccType: null as string | null,
  ccHint: DEFAULT_CC_HINT,
  eprotectResponse: null as import('./types').EprotectResponse | null,
  isProcessing: false,
  isLoading: false,
  showForm: false,
  submitSuccess: false,
  error: null as string | null,
};

export const useBillingStore = create<BillingStore>((set, get) => ({
  ...initialState,

  loadCardInfo: async () => {
    if (get().isLoading) {
      return;
    }

    set({
      isLoading: true,
      isProcessing: false,
      error: null,
      submitSuccess: false,
      nyDisclaimerAccepted: false,
      eprotectResponse: null,
      ccType: null,
    });

    try {
      const response = await getCreditCardInfoV3();
      const existingCard = asRecord(response) as ExistingCardInfo;
      const formValues = toFormValues(existingCard);

      set({
        existingCard,
        formValues,
        originalFormValues: formValues,
        ccHint: buildCcHint(asString(existingCard.CardNumber, '')),
        submitSuccess: false,
        nyDisclaimerAccepted: false,
        eprotectResponse: null,
        ccType: null,
        isLoading: false,
      });
    } catch (error: unknown) {
      set({
        isLoading: false,
        error: normalizeErrorMessage(
          error,
          'Unable to load credit card information right now. Please try again.'
        ),
      });
    }
  },

  updateField: (field, value) => {
    const nextValues = {
      ...get().formValues,
      [field]: value,
    };

    set({ formValues: nextValues, error: null });

    if (field === 'CardNumber') {
      get().validateCardNumber(value);
    }
  },

  validateCardNumber: value => {
    const brand = detectCardBrand(value);
    const isLengthValid = isSupportedLength(value, brand);
    const isCardValid = isValidCardNumber(value);

    if (!value) {
      set({ ccType: null });
      return false;
    }

    set({
      ccType: brand,
      ccHint: !brand
        ? 'Unsupported card number.'
        : !isLengthValid
          ? 'Card length does not match the detected brand.'
          : isCardValid
            ? 'Card number looks valid.'
            : 'Card number failed validation. Please review and try again.',
    });

    return isCardValid;
  },

  setNYDisclaimer: accepted => {
    set({ nyDisclaimerAccepted: accepted, error: null });
  },

  setEprotectResponse: (response: EprotectResponse) => {
    set({ eprotectResponse: response, error: null });
  },

  submitCardUpdate: async billingInfo => {
    if (get().isProcessing) {
      return;
    }

    if (!get().nyDisclaimerAccepted) {
      set({ error: 'Please accept the NY disclaimer before submitting card updates.' });
      return;
    }

    const cardNumber = get().formValues.CardNumber;
    if (!get().validateCardNumber(cardNumber)) {
      set({ error: 'Please enter a valid card number before submitting.' });
      return;
    }

    set({ isProcessing: true, error: null, submitSuccess: false });

    try {
      const payload = {
        ...buildPayloadFromForm(get().formValues, get().ccType),
        ...billingInfo,
      };

      const eprotectResponse = get().eprotectResponse;
      if (eprotectResponse) {
        payload.LowValueToken = asString(
          eprotectResponse.paypageRegistrationId,
          payload.LowValueToken || ''
        );
        payload.ReportGroup = asString(eprotectResponse.reportGroup, payload.ReportGroup || 'MPBI');
        payload.VantivTxnId = asString(eprotectResponse.vantivTxnId, payload.VantivTxnId || '');
        payload.BinNumber = asString(eprotectResponse.bin, payload.BinNumber || '');
        payload.ResponseCode = asString(eprotectResponse.response, payload.ResponseCode || '');
        payload.ResponseMessage = asString(eprotectResponse.message, payload.ResponseMessage || '');
        payload.ResponseTime = asString(eprotectResponse.responseTime, payload.ResponseTime || '');
      }

      const updateResult = await putCreditCardInfoV3(payload);
      const statusCode = asString(asRecord(updateResult).StatusCode, '');
      if (statusCode && statusCode !== 'SUCCESS') {
        throw new Error('Credit card update was not successful.');
      }

      const updatedUser = await getAccountInfo();
      setSessionUser(updatedUser);
      useRefillsStore.getState().refreshAfterCardUpdate(updatedUser);

      const formValues = get().formValues;

      set({
        isProcessing: false,
        submitSuccess: true,
        eprotectResponse: null,
        error: null,
        originalFormValues: formValues,
      });
    } catch (error: unknown) {
      set({
        isProcessing: false,
        error: normalizeErrorMessage(
          error,
          'Unable to update your credit card right now. Please confirm your information and try again.'
        ),
      });
    }
  },

  toggleForm: () => {
    const nextShowForm = !get().showForm;
    set({ showForm: nextShowForm, error: null });
    if (!nextShowForm) {
      get().clearForm();
    }
  },

  clearForm: () => {
    const existingCard = get().existingCard;
    const formValues = toFormValues(existingCard);
    set({
      formValues,
      originalFormValues: formValues,
      nyDisclaimerAccepted: false,
      ccType: null,
      eprotectResponse: null,
      error: null,
      submitSuccess: false,
      ccHint: buildCcHint(asString(existingCard?.CardNumber, '')),
    });
  },

  isDirty: () => {
    const { formValues, originalFormValues } = get();
    return JSON.stringify(formValues) !== JSON.stringify(originalFormValues);
  },

  reset: () => set(initialState),
}));
